import {useEffect, useState} from "react";
import {Stack, Switch, FormControlLabel, Select, MenuItem, SelectChangeEvent, Chip} from "@mui/material";
import {DateRange} from "@mui/x-date-pickers-pro";
import CalendarDialog from "../DateRangeCalendar/CalendarDialog";
import dayjs from "dayjs";
import {useTeamProgressContext, DateRangeType} from "../../context/TeamProgressContext";
import {useSSTranslation} from "@/i18n/useSSTranslation";

/**
 * Filters Component
 * 
 * Matches legacy Filters component exactly:
 * - Switch for archived users (left)
 * - Select dropdown for date range presets (right)
 * - Chip display for custom date range with delete
 * - Calendar dialog for custom date selection
 */
export function Filters() {
	const {updateFilters, filters, allSectionsLoaded, sectionsLoaded} = useTeamProgressContext();
	const {archivedUsers, dateRangeType, dateFrom, dateTo} = filters;
	const [openDialog, setOpenDialog] = useState<boolean>(false);
	const [enableFilters, setEnableFilters] = useState<boolean>(false);
	const isCustomDateRange = dateRangeType === "customDateRange";
	const {t} = useSSTranslation();

	useEffect(() => {
		const sectionsLoadedState = allSectionsLoaded();
		setEnableFilters(sectionsLoadedState);
	}, [sectionsLoaded, allSectionsLoaded]);

	const handleChangeDateRangeFilter = (event: SelectChangeEvent<DateRangeType>) => {
		const selectedOption = event.target.value as DateRangeType;
		if (selectedOption === "customDateRange") {
			setOpenDialog(true);
			return;
		}

		// Use updateFilters (Partial) instead of setFilters
		updateFilters({dateRangeType: selectedOption});
	};

	const handleChangeDateRange = (value: DateRange<Date>) => {
		updateFilters({
			dateRangeType: "customDateRange",
			dateFrom: dayjs(value[0]).format("YYYY-MM-DD"),
			dateTo: dayjs(value[1]).format("YYYY-MM-DD")
		});
	};

	const handleClearDateRange = () => {
		updateFilters({dateRangeType: "lastWeek"});
	};

	const handleCloseDialog = () => setOpenDialog(false);

	const handleChangeArchivedUserToggle = (_: React.SyntheticEvent, checked: boolean) => {
		updateFilters({archivedUsers: checked});
	};

	return (
		<Stack direction={{xs: "column", md: "row"}}>
			<FormControlLabel
				control={<Switch checked={archivedUsers} />}
				label={t("team_progress_filter_archived_users", "Archived Users")}
				onChange={handleChangeArchivedUserToggle}
				disabled={!enableFilters}
			/>
			{
				!isCustomDateRange &&
				<Select
					onChange={handleChangeDateRangeFilter}
					value={dateRangeType}
					disabled={!enableFilters}
				>
					<MenuItem value="lastWeek">{t("team_progress_filter_last_week", "Last Week")}</MenuItem>
					<MenuItem value="lastMonth">{t("team_progress_filter_last_month", "Last Month")}</MenuItem>
					<MenuItem value="lastYear">{t("team_progress_filter_last_year", "Last Year")}</MenuItem>
					<MenuItem value="customDateRange">{t("team_progress_filter_custom_date_range", "Custom Date Range")}</MenuItem>
				</Select>
			}
			{
				isCustomDateRange &&
				<Chip
					variant="outlined"
					color="primary"
					label={`${dateFrom ? dayjs(dateFrom).format("MM/DD/YYYY") : ""} - ${dateTo ? dayjs(dateTo).format("MM/DD/YYYY") : ""}`}
					onDelete={handleClearDateRange}
					sx={(theme) => ({
						"& .MuiChip-label": {
							color: theme.palette.common.white
						}
					})}
					disabled={!enableFilters}
				/>
			}
			{
				openDialog &&
				<CalendarDialog
					onAcceptDateRange={handleChangeDateRange}
					onClose={handleCloseDialog}
				/>
			}
		</Stack>
	);
}
